import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CRow,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCheck,
  cilDescription,
  cilEnvelopeClosed,
  cilFile,
  cilLocationPin,
  cilShieldAlt,
  cilUser,
} from "@coreui/icons";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  checkPublicProformaLead,
  submitPublicProformaRequest,
} from "src/api/commonapi";
import CustomImage from "src/views/CommonFunctions/CustomImage";
import { Routes } from "src/constants/api_url";
import { notification } from "src/constants/message";
import en from "react-phone-number-input/locale/en";
import { CountrySelect } from "src/views/CommonFunctions/common";
import "./ProformaRequestForm.css";

const ALLOWED_UPLOAD_EXTENSIONS = ["jpg", "jpeg", "png", "pdf"];
const ALLOWED_UPLOAD_ACCEPT =
  "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf";

const validateProformaFile = async (file) => {
  if (!file) return { ok: true };
  const ext = String(file.name || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
    return {
      ok: false,
      message: `Invalid file. Only JPG, PNG, and PDF files are allowed.`,
    };
  }
  const bytes = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
  let header = "";
  for (let i = 0; i < bytes.length; i++) header += String.fromCharCode(bytes[i]);
  const isPdf = header.includes("%PDF-");
  const isPng =
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47;
  const isJpg = bytes[0] === 0xff && bytes[1] === 0xd8;
  if (!isPdf && !isPng && !isJpg) {
    return {
      ok: false,
      message: `Invalid file. Only JPG, PNG, and PDF files are allowed.`,
    };
  }
  return { ok: true };
};

const formSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Please enter a valid Registered Email ID ")
    .required("Registered Email ID is required"),
  contact_name: yup.string().trim().required("Contact person name is required"),
  mobile: yup.string().trim().required("Contact number is required"),
  home_delivery: yup.boolean(),
  address: yup.string().trim().when("home_delivery", {
    is: true,
    then: (schema) => schema.required("Postal address is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  city: yup.string().trim().when("home_delivery", {
    is: true,
    then: (schema) => schema.required("City is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  pincode: yup.string().trim().when("home_delivery", {
    is: true,
    then: (schema) => schema.required("Pincode is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  invoice_favour_to: yup.string().trim().notRequired(),
  gst_number: yup.string().trim().notRequired(),
});

const defaultValues = {
  email: "",
  invoice_favour_to: "",
  gst_number: "",
  contact_name: "",
  mobile: "",
  home_delivery: false,
  address: "",
  city: "",
  pincode: "",
};

const steps = [
  { id: "pf-email", label: "Email" },
  { id: "pf-contact", label: "Contact" },
  { id: "pf-address", label: "Address" },
  { id: "pf-documents", label: "Documents" },
];

const Icons = {
  mail: cilEnvelopeClosed,
  user: cilUser,
  pin: cilLocationPin,
  receipt: cilDescription,
  file: cilFile,
  check: cilCheck,
  shield: cilShieldAlt,
};

const SectionHeader = ({ icon, tone, title, subtitle }) => (
  <div className="pf-section-head d-flex align-items-start gap-3 mb-3">
    <span
      className={`pf-icon pf-icon-${tone} d-flex align-items-center justify-content-center flex-shrink-0`}
    >
      <CIcon icon={Icons[icon]} />
    </span>
    <div>
      <h2 className="h6 fw-bold mb-0">{title}</h2>
      {subtitle ? <p className="mb-0 mt-1">{subtitle}</p> : null}
    </div>
  </div>
);

const buildDocuments = (applicants) => {
  let docs = [];
  let groupByName = {};
  let nextGroup = 0;
  (applicants || []).forEach((a) => {
    let key = String(a.applicant_name || "").trim();
    let group;
    if (!key) {
      group = nextGroup++;
    } else if (groupByName[key] != undefined) {
      group = groupByName[key];
    } else {
      group = nextGroup++;
      groupByName[key] = group;
    }
    docs.push({
      certificate_id: a.certificate_id || 0,
      applicant_name: key,
      document_name: a.document_name || "",
      prefix: a.prefix || "",
      group,
    });
  });
  return docs;
};

const ProformaRequestForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [leadVerified, setLeadVerified] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [files, setFiles] = useState([]);
  const fileInputsRef = useRef({});
  const [submitResponse, setSubmitResponse] = useState(null);
  const [shipCC, setShipCC] = useState({ code: 91, iso: "IN" });
  const [currentStep, setCurrentStep] = useState(0);

  const methods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = methods;

  const homeDelivery = watch("home_delivery");

  const showToast = (type, message) => {
    notification({ type, message })();
  };

  const handleCheckEmail = async () => {
    const email = getValues("email");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      trigger("email");
      return;
    }
    setLoading(true);
    try {
      const resp = await checkPublicProformaLead({ email: email.trim() });
      if (resp?.status == 1) {
        const docs = buildDocuments(resp.data.applicants || []);
        if (docs.length == 0) {
          setLeadData(null);
          setLeadVerified(false);
          setCurrentStep(0);
          showToast("error", "No documents found for this email.");
          return;
        }
        setLeadData(resp.data);
        setLeadVerified(true);
        setCurrentStep(1);
        setValue(
          "contact_name",
          resp.data.l_name_prefix
            ? `${resp.data.l_name_prefix} ${resp.data.l_name}`
            : resp.data.l_name || "",
        );
        setValue(
          "invoice_favour_to",
          resp.data.l_name || resp.data.proforma_c_name || "",
        );
        setValue(
          "gst_number",
          resp.data.m_c_gst || resp.data.proforma_c_gst_no || "",
        );
        setValue("mobile", resp.data.co_no || "");
        setValue("address", resp.data.l_address || "");
        setValue("city", resp.data.l_city || "");
        setValue("pincode", resp.data.l_pincode || "");
        clearAllFileInputs();
        setDocuments(docs);
        setFiles(docs.map(() => null));
      } else {
        showToast("error", resp?.message || "Email not found");
      }
    } catch (e) {
      console.log("checkProformaEmail", e);
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStepClick = (index) => {
    if (index > 0 && !leadVerified) return;
    setCurrentStep(index);
    const target = document.getElementById(steps[index].id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleApplicantChange = (group, value) => {
    setDocuments((prev) =>
      prev.map((d) => (d.group === group ? { ...d, applicant_name: value } : d)),
    );
  };

  const clearFileInput = (index) => {
    const input = fileInputsRef.current[index];
    if (input) input.value = "";
  };

  const clearAllFileInputs = () => {
    Object.keys(fileInputsRef.current).forEach((key) => {
      const input = fileInputsRef.current[key];
      if (input) input.value = "";
    });
  };

  const handleFileChange = async (index, file, inputEl) => {
    if (!file) {
      setFiles((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
      return;
    }
    const check = await validateProformaFile(file);
    if (!check.ok) {
      setFiles((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
      if (inputEl) inputEl.value = "";
      else clearFileInput(index);
      showToast("error", check.message);
      return;
    }
    setFiles((prev) => {
      const next = [...prev];
      next[index] = file;
      return next;
    });
  };

  const onSubmit = async (formData) => {
    if (!leadVerified || !leadData) {
      handleCheckEmail();
      return;
    }
    if (leadData?.is_gst == 1) {
      if (!formData.invoice_favour_to || !String(formData.invoice_favour_to).trim()) {
        showToast("error", "Invoice Favour To is required for GST");
        return;
      }
      if (!formData.gst_number || !String(formData.gst_number).trim()) {
        showToast("error", "GST number is required for GST");
        return;
      }
    }
    const mobileDigits = String(formData.mobile || "").replace(/[^0-9]/g, "");
    if (shipCC.iso == "IN" && mobileDigits.length != 10) {
      showToast("error", "Mobile number must be 10 digits for India (+91)");
      return;
    }
    if (shipCC.iso != "IN" && (mobileDigits.length < 7 || mobileDigits.length > 15)) {
      showToast("error", "Mobile number must be 7 to 15 digits");
      return;
    }
    if (documents.length == 0) {
      showToast("error", "No documents found. Please verify your email again.");
      return;
    }
    for (let i = 0; i < documents.length; i++) {
      if (!documents[i].applicant_name || documents[i].applicant_name.trim() === "") {
        showToast(
          "error",
          `Document holder's name is required for ${documents[i].document_name || `document ${i + 1}`}`,
        );
        return;
      }
      if (!files[i]) {
        showToast(
          "error",
          `Please upload a file for ${documents[i].document_name || `document ${i + 1}`}`,
        );
        return;
      }
      const check = await validateProformaFile(files[i]);
      if (!check.ok) {
        setFiles((prev) => {
          const next = [...prev];
          next[i] = null;
          return next;
        });
        clearFileInput(i);
        showToast("error", check.message);
        return;
      }
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("lead_id", leadData.lead_id);
      fd.append("email", leadData.l_email || getValues("email"));
      fd.append("contact_name", formData.contact_name.trim());
      fd.append("mobile", formData.mobile.trim());
      fd.append("ship_countryCode", shipCC.code || 91);
      fd.append("ship_country_code", shipCC.iso || "IN");
      fd.append("home_delivery", homeDelivery ? 1 : 0);
      fd.append("address", homeDelivery ? (formData.address || "").trim() : "");
      fd.append("city", homeDelivery ? (formData.city || "").trim() : "");
      fd.append("pincode", homeDelivery ? (formData.pincode || "").trim() : "");
      fd.append("invoice_favour_to", (formData.invoice_favour_to || "").trim());
      fd.append("gst_number", (formData.gst_number || "").trim());
      fd.append(
        "documents",
        JSON.stringify(
          documents.map((d) => ({
            certificate_id: d.certificate_id || 0,
            applicant_name: d.applicant_name.trim(),
            document_name: d.document_name,
            prefix: d.prefix,
          })),
        ),
      );
      files.forEach((file) => {
        if (file) fd.append("files", file);
      });
      const resp = await submitPublicProformaRequest(fd);
      if (resp?.status == 1) {
        const trackingId = resp?.data?.tracking_id;
        if (trackingId) {
          navigate(`/lead-payment/${encodeURIComponent(trackingId)}`, {
            replace: true,
          });
        } else {
          setSubmitResponse(resp);
        }
      } else {
        showToast("error", resp?.message || "Failed to submit request");
      }
    } catch (e) {
      console.log("submitProformaRequest", e);
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const groups = [];
  documents.forEach((d) => {
    let group = groups.find((g) => g.group === d.group);
    if (!group) {
      group = {
        group: d.group,
        applicant_name: d.applicant_name,
        items: [],
      };
      groups.push(group);
    }
    group.items.push(d);
  });

  const renderLogo = () => (
    <div className="pf-logo bg-white rounded-circle overflow-hidden d-flex align-items-center justify-content-center flex-shrink-0">
      <CustomImage
        src={`${Routes.API_HOSTNAME}/images/adminlogo/loginlogo.png`}
        width="100%"
        height="100%"
        style={{ objectFit: "contain" }}
      />
    </div>
  );

  const renderTrust = () => (
    <div className="pf-trust d-inline-flex align-items-center gap-2 flex-shrink-0 rounded-pill fw-semibold align-self-start align-self-md-center">
      <CIcon icon={Icons.shield} />
      <span>Secure · Fast · Reliable</span>
    </div>
  );

  const renderHero = (title, subtitle) => (
    <CCard className="pf-hero border-0 text-white">
      <CCardBody className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 p-4">
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
          {renderLogo()}
          <div>
            <h1 className="fw-bold mb-0">{title}</h1>
            <p className="mb-0 mt-1">{subtitle}</p>
          </div>
        </div>
        {renderTrust()}
      </CCardBody>
    </CCard>
  );

  if (submitResponse) {
    return (
      <div className="pf-page min-vh-100 py-3 py-md-4 px-2 px-md-3 pb-4 pb-md-5">
        <div className="mx-auto w-100" style={{ maxWidth: "980px" }}>
          {renderHero(
            "Request submitted",
            "We have received your documents and will take it from here.",
          )}
          <CCard className="pf-card mt-3">
            <CCardBody className="pf-success text-center">
              <div className="pf-success-icon d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3">
                <CIcon icon={Icons.check} />
              </div>
              <h2 className="fw-bold mb-0">Thank you!</h2>
              <p className="text-body mt-2 mb-0" style={{ fontSize: "14px" }}>
                Your request has been submitted successfully.
              </p>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                Your proforma will be shared on your email.
              </p>
            </CCardBody>
          </CCard>
        </div>
      </div>
    );
  }

  return (
    <div className="pf-page min-vh-100 py-3 py-md-4 px-2 px-md-3 pb-4 pb-md-5">
      <div className="mx-auto w-100" style={{ maxWidth: "980px" }}>
        {renderHero(
          "Submit Your Document and Proceed to Payment",
          "Submit your document to receive an invoice. Processing will begin once your payment is received.",
        )}

        <nav
          className="pf-steps d-flex my-3 bg-white"
          aria-label="Form progress"
        >
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isDone = index < currentStep || (index === 0 && leadVerified);
            return (
              <button
                key={step.id}
                type="button"
                className={`pf-step btn btn-link flex-fill d-flex align-items-center justify-content-center border-0 text-decoration-none${isActive ? " active" : ""}${isDone ? " done" : ""}`}
                onClick={() => handleStepClick(index)}
                disabled={index > 0 && !leadVerified}
              >
                <span className="pf-step-num d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle">
                  {isDone ? <CIcon icon={Icons.check} style={{ width: 12, height: 12 }} /> : index + 1}
                </span>
                <span className="pf-step-label">{step.label}</span>
              </button>
            );
          })}
        </nav>

        <CCard className="pf-card">
          <CCardBody className="p-3 p-md-4">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                  <section className="pf-section" id="pf-email">
                <SectionHeader
                  icon="mail"
                  tone="blue"
                  title="Email verification"
                  subtitle="Enter the registered email address where you receive information from us."
                />
                <CRow className="g-3 align-items-end">
                  <CCol xs={12} md={8}>
                    <CFormLabel className="pf-label">
                      Registered Email ID <span className="text-danger">*</span>
                    </CFormLabel>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <CFormInput
                          {...field}
                          type="email"
                          className="pf-input"
                          placeholder="you@example.com"
                          disabled={leadVerified}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (!leadVerified) handleCheckEmail();
                            }
                          }}
                        />
                      )}
                    />
                    {errors?.email && (
                      <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                        {errors?.email?.message}
                      </div>
                    )}
                  </CCol>
                  <CCol xs={12} md={4}>
                    <CButton
                      type="button"
                      color="primary"
                      className={`pf-verify-btn${leadVerified ? " is-verified" : ""}`}
                      disabled={loading || leadVerified}
                      onClick={handleCheckEmail}
                    >
                      {leadVerified ? (
                        <>
                          <CIcon icon={Icons.check} style={{ width: 14, height: 14 }} />
                          Verified
                        </>
                      ) : loading ? (
                        "Checking..."
                      ) : (
                        "Verify email"
                      )}
                    </CButton>
                  </CCol>
                </CRow>
                {leadVerified && (
                  <CAlert
                    color="success"
                    className="pf-alert d-flex align-items-center gap-2 py-2 px-3 mt-3 mb-0"
                    style={{
                      backgroundColor: "#e9f8f0",
                      borderColor: "#7cb89b",
                      color: "#177a4c",
                      fontSize: "12.5px",
                      fontWeight: 500,
                    }}
                  >
                    <CIcon icon={Icons.check} style={{ width: 14, height: 14 }} />
                    <span>Email verified. Please complete the details below.</span>
                  </CAlert>
                )}
              </section>

              {leadVerified && (
                <>
                  <section className="pf-section" id="pf-contact">
                    <SectionHeader
                      icon="user"
                      tone="blue"
                      title="Contact information"
                      subtitle="Who should we contact about this request?"
                    />
                    <CRow className="g-3">
                      <CCol xs={12} md={4}>
                        <CFormLabel className="pf-label">
                          Contact person name <span className="text-danger">*</span>
                        </CFormLabel>
                        <Controller
                          name="contact_name"
                          control={control}
                          render={({ field }) => (
                            <CFormInput
                              {...field}
                              className="pf-input"
                              placeholder="e.g. Prashant Agrawane"
                            />
                          )}
                        />
                        {errors?.contact_name && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            {errors?.contact_name?.message}
                          </div>
                        )}
                      </CCol>
                      <CCol xs={12} md={4}>
                        <CFormLabel className="pf-label">
                          Country <span className="text-danger">*</span>
                        </CFormLabel>
                        <div className="pf-country">
                          <CountrySelect
                            labels={en}
                            value={shipCC.iso}
                            onChange={(countryCode, country) => {
                              setShipCC({ code: countryCode, iso: country });
                              setValue("mobile", "");
                            }}
                          />
                        </div>
                      </CCol>
                      <CCol xs={12} md={4}>
                        <CFormLabel className="pf-label">
                          Contact number <span className="text-danger">*</span>
                        </CFormLabel>
                        <Controller
                          name="mobile"
                          control={control}
                          render={({ field }) => (
                            <CFormInput
                              {...field}
                              className="pf-input"
                              placeholder="Phone number"
                              onChange={(e) => {
                                let v = e.target.value.replace(/[^0-9]/g, "");
                                v =
                                  shipCC.iso == "IN"
                                    ? v.slice(0, 10)
                                    : v.slice(0, 15);
                                field.onChange(v);
                              }}
                            />
                          )}
                        />
                        {errors?.mobile && (
                          <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                            {errors?.mobile?.message}
                          </div>
                        )}
                      </CCol>
                    </CRow>
                  </section>

                  {leadData?.is_gst == 1 && (
                    <section className="pf-section" id="pf-invoice">
                      <SectionHeader
                        icon="receipt"
                        tone="amber"
                        title="Invoice details"
                        subtitle="GST details required for your proforma invoice."
                      />
                      <CRow className="g-3">
                        <CCol xs={12} md={6}>
                          <CFormLabel className="pf-label">
                            Invoice favour to <span className="text-danger">*</span>
                          </CFormLabel>
                          <Controller
                            name="invoice_favour_to"
                            control={control}
                            render={({ field }) => (
                              <CFormInput
                                {...field}
                                className="pf-input"
                                placeholder="Invoice favour to"
                              />
                            )}
                          />
                        </CCol>
                        <CCol xs={12} md={6}>
                          <CFormLabel className="pf-label">
                            GST number <span className="text-danger">*</span>
                          </CFormLabel>
                          <Controller
                            name="gst_number"
                            control={control}
                            render={({ field }) => (
                              <CFormInput
                                {...field}
                                className="pf-input"
                                placeholder="GST number"
                              />
                            )}
                          />
                        </CCol>
                      </CRow>
                    </section>
                  )}

                  <section className="pf-section" id="pf-address">
                    <SectionHeader
                      icon="pin"
                      tone="green"
                      title="Postal address"
                      subtitle="Choose whether you need the documents delivered to your address."
                    />
                    <CCard
                      className="pf-delivery-toggle mb-3"
                      style={{ backgroundColor: "#f8fbff", border: "1px solid #94a3b8" }}
                    >
                      <CCardBody className="p-3">
                        <Controller
                          name="home_delivery"
                          control={control}
                          render={({ field }) => (
                            <CFormCheck
                              id="pf-home-delivery"
                              label="If decided postal address, click here."
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                      </CCardBody>
                    </CCard>
                    {homeDelivery && (
                      <CRow className="g-3">
                        <CCol xs={12}>
                          <CFormLabel className="pf-label">
                            Address <span className="text-danger">*</span>
                          </CFormLabel>
                          <Controller
                            name="address"
                            control={control}
                            render={({ field }) => (
                              <CFormTextarea
                                {...field}
                                rows={3}
                                className="pf-input"
                                placeholder="Enter your full postal address"
                              />
                            )}
                          />
                          {errors?.address && (
                            <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                              {errors?.address?.message}
                            </div>
                          )}
                        </CCol>
                        <CCol xs={12} md={6}>
                          <CFormLabel className="pf-label">
                            City <span className="text-danger">*</span>
                          </CFormLabel>
                          <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                              <CFormInput
                                {...field}
                                className="pf-input"
                                placeholder="Enter city"
                              />
                            )}
                          />
                          {errors?.city && (
                            <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                              {errors?.city?.message}
                            </div>
                          )}
                        </CCol>
                        <CCol xs={12} md={6}>
                          <CFormLabel className="pf-label">
                            Pincode <span className="text-danger">*</span>
                          </CFormLabel>
                          <Controller
                            name="pincode"
                            control={control}
                            render={({ field }) => (
                              <CFormInput
                                {...field}
                                className="pf-input"
                                placeholder="Enter pincode"
                              />
                            )}
                          />
                          {errors?.pincode && (
                            <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                              {errors?.pincode?.message}
                            </div>
                          )}
                        </CCol>
                      </CRow>
                    )}
                  </section>

                  <section className="pf-section" id="pf-documents">
                    <SectionHeader
                      icon="file"
                      tone="purple"
                      title="Document holder's name"
                      subtitle="Enter the name exactly as it appears on each document."
                    />
                    <CAlert
                      color="info"
                      className="pf-alert py-2 px-3 mb-3"
                      style={{
                        borderStyle: "dashed",
                        backgroundColor: "#eef7ff",
                        borderColor: "#5b9bd5",
                        color: "#000000",
                        fontSize: "12.5px",
                        lineHeight: 1.45,
                      }}
                    >
                      Double-check the spelling. It should match the name on the document exactly.
                    </CAlert>
                    {groups.length == 0 && (
                      <CAlert
                        color="danger"
                        className="pf-alert py-2 px-3 mb-0"
                        style={{
                          backgroundColor: "#fff6f6",
                          borderColor: "#d08a8a",
                          color: "#b23b3b",
                          fontSize: "13px",
                        }}
                      >
                        No documents found for this email. Please verify again.
                      </CAlert>
                    )}
                    {groups.map((group, groupIndex) => (
                      <CCard
                        key={group.group}
                        className={`pf-holder ${
                          groupIndex === groups.length - 1 ? "mb-0" : "mb-3"
                        }`}
                        style={{ backgroundColor: "#f8fbff", border: "1px solid #94a3b8" }}
                      >
                        <CCardBody className="p-3">
                          <div className="d-flex align-items-start align-items-md-center gap-2">
                            <CBadge
                              color="primary"
                              className="pf-holder-badge rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 p-0"
                            >
                              {groupIndex + 1}
                            </CBadge>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <CFormLabel className="pf-label mb-1">
                                Applicant name <span className="text-danger">*</span>
                              </CFormLabel>
                              <CFormInput
                                value={group.applicant_name}
                                className="pf-input"
                                placeholder="Enter full name"
                                onChange={(e) =>
                                  handleApplicantChange(group.group, e.target.value)
                                }
                              />
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
                            {group.items.map((item, itemIndex) => {
                              const docIndex = documents.indexOf(item);
                              return (
                                <CRow
                                  className="pf-doc-row align-items-center g-2 bg-white p-2 mx-0"
                                  style={{ border: "1px solid var(--pf-border-soft)" }}
                                  key={docIndex}
                                >
                                  <CCol xs={12} md={5} className="pf-doc-name-col">
                                    <div className="pf-doc-name d-flex align-items-center gap-2 fw-semibold text-body text-break">
                                      {group.items.length > 1 && (
                                        <CBadge
                                          color="light"
                                          className="pf-doc-num rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 p-0"
                                          style={{
                                            width: 20,
                                            height: 20,
                                            backgroundColor: "#eef4fa",
                                            color: "#228ce4",
                                            fontSize: "11px",
                                            fontWeight: 700,
                                          }}
                                        >
                                          {itemIndex + 1}
                                        </CBadge>
                                      )}
                                      <span>{item.document_name || "Document"}</span>
                                    </div>
                                  </CCol>
                                  <CCol xs={12} md={7} className="pf-doc-upload-col">
                                    <CFormInput
                                      type="file"
                                      className="pf-file-input"
                                      accept={ALLOWED_UPLOAD_ACCEPT}
                                      aria-label={`Upload ${item.document_name || "document"}`}
                                      ref={(el) => {
                                        fileInputsRef.current[docIndex] = el;
                                      }}
                                      onChange={(e) =>
                                        handleFileChange(
                                          docIndex,
                                          e.target.files?.[0] || null,
                                          e.target,
                                        )
                                      }
                                    />
                                    {files[docIndex] && (
                                      <span
                                        className="d-block mt-1 text-muted text-truncate"
                                        style={{ fontSize: "11.5px" }}
                                      >
                                        {files[docIndex].name}
                                      </span>
                                    )}
                                  </CCol>
                                </CRow>
                              );
                            })}
                          </div>
                        </CCardBody>
                      </CCard>
                    ))}
                  </section>

                  <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 pt-4">
                    <CButton
                      type="submit"
                      color="primary"
                      className="pf-submit"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit & Check Your Email"}
                    </CButton>
                    <div
                      className="d-flex align-items-start gap-2 text-muted"
                      style={{ fontSize: "12px" }}
                    >
                      <span className="pf-help-icon d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle">
                        <CIcon icon={Icons.shield} style={{ width: 16, height: 16 }} />
                      </span>
                      <div>
                        <strong
                          className="d-block text-body"
                          style={{ fontSize: "12.5px" }}
                        >
                          Need help?
                        </strong>
                        <p className="mb-0" style={{ lineHeight: 1.4 }}>
                          Our support team is here for you.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </form>
          </FormProvider>
          </CCardBody>
        </CCard>
      </div>
    </div>
  );
};

export default ProformaRequestForm;






